/**
 * ExistingDebtPaymentTable - Payment Table Section for Existing Debt Inputs
 *
 * Contains:
 * - Payment schedule table with all payment rows
 * - Keyboard navigation
 * - Lock controls (May/November dates, auto-multiply)
 * - Fiscal year totals column
 * - Running totals row
 */

import { Box, Button, ButtonGroup, HStack, Table, Text } from '@chakra-ui/react';
import Decimal from 'decimal.js-light';
import type React from 'react';
import { CurrencyInput, PercentInput } from '~/components/inputs';
import type {
  DateInputsForm,
  DateRecord,
  ExistingDebtInputForm,
  ExistingDebtPaymentForm,
} from '~/types';
import { formatCurrency } from '~/utils/formatting-utils';

interface ExistingDebtPaymentTableProps {
  values: ExistingDebtInputForm;
  dateInputs?: DateInputsForm;
  availablePaymentDates: DateRecord[];
  calculatedInterestPayments: Map<number, number>;
  hasCalculatedReverseAmortization: boolean;
  fiscalYearTotals: Map<string, number>;
  fiscalYearColumnTitle: string;
  runningTotals: {
    totalPrincipal: number;
    totalInterest: number;
    totalPayment: number;
  };
  autoMultiplyEnabled: boolean;
  setAutoMultiplyEnabled: (enabled: boolean) => void;
  lockMayDates: boolean;
  setLockMayDates: (locked: boolean) => void;
  lockNovemberDates: boolean;
  setLockNovemberDates: (locked: boolean) => void;
  isPaymentDateLocked: (paymentDate: string) => boolean;
  getInputRef: (rowIndex: number, colIndex: number) => React.RefObject<HTMLInputElement>;
  handlePaymentChange: (index: number, field: keyof ExistingDebtPaymentForm, value: string) => void;
  handlePrincipalPaymentBlur: (index: number) => void;
  handleInterestRateBlur: () => void;
  handleKeyDown: (e: React.KeyboardEvent, rowIndex: number, colIndex: number) => void;
  isDisabled: boolean;
  // Focus management
  handleInputFocus?: (rowIndex: number, colIndex: number) => void;
  handleInputBlur?: () => void;
  isNavigating?: () => boolean;
}

export const ExistingDebtPaymentTable: React.FC<ExistingDebtPaymentTableProps> = ({
  values,
  dateInputs,
  availablePaymentDates,
  calculatedInterestPayments,
  hasCalculatedReverseAmortization,
  fiscalYearTotals,
  fiscalYearColumnTitle,
  runningTotals,
  autoMultiplyEnabled,
  setAutoMultiplyEnabled,
  lockMayDates,
  setLockMayDates,
  lockNovemberDates,
  setLockNovemberDates,
  isPaymentDateLocked,
  getInputRef,
  handlePaymentChange,
  handlePrincipalPaymentBlur,
  handleInterestRateBlur,
  handleKeyDown,
  isDisabled,
  // Focus management
  handleInputFocus,
  handleInputBlur,
  isNavigating,
}) => {
  return (
    <Box>
      <HStack justify="space-between" align="center" mb={4}>
        <Text fontWeight="medium">Payment Schedule</Text>
        <HStack gap={2}>
          <ButtonGroup attached size="sm">
            <Button
              colorPalette={autoMultiplyEnabled ? 'blue' : undefined}
              variant={autoMultiplyEnabled ? 'solid' : 'outline'}
              onClick={() => setAutoMultiplyEnabled(true)}
            >
              Enter In 1000's
            </Button>
            <Button
              colorPalette={!autoMultiplyEnabled ? 'blue' : undefined}
              variant={!autoMultiplyEnabled ? 'solid' : 'outline'}
              onClick={() => setAutoMultiplyEnabled(false)}
            >
              Enter Exact Amount
            </Button>
          </ButtonGroup>
          <ButtonGroup attached size="sm">
            <Button
              colorPalette={lockMayDates ? 'blue' : undefined}
              variant={lockMayDates ? 'solid' : 'outline'}
              onClick={() => setLockMayDates(!lockMayDates)}
            >
              Lock May Dates
            </Button>
            <Button
              colorPalette={lockNovemberDates ? 'blue' : undefined}
              variant={lockNovemberDates ? 'solid' : 'outline'}
              onClick={() => setLockNovemberDates(!lockNovemberDates)}
            >
              Lock November Dates
            </Button>
          </ButtonGroup>
        </HStack>
      </HStack>

      {values.payments.length === 0 ? (
        <Text fontSize="sm" color="gray.500" textAlign="center" py={8}>
          No payment entries. Enter debt name and final payment date, then click "Generate Schedule"
          to get started.
        </Text>
      ) : (
        <Box overflowX="auto">
          <Table.Root size="sm">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>Payment Date</Table.ColumnHeader>
                <Table.ColumnHeader>Interest Rate</Table.ColumnHeader>
                <Table.ColumnHeader>Principal Payment</Table.ColumnHeader>
                <Table.ColumnHeader>Principal Interest</Table.ColumnHeader>
                <Table.ColumnHeader>Interest Payment</Table.ColumnHeader>
                <Table.ColumnHeader>Total Payment</Table.ColumnHeader>
                {dateInputs && <Table.ColumnHeader>{fiscalYearColumnTitle}</Table.ColumnHeader>}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {values.payments
                .map((payment, originalIndex) => ({ payment, originalIndex }))
                .filter(({ payment }) => !isPaymentDateLocked(payment.paymentDate))
                .map(({ payment, originalIndex }) => {
                  // Parse numeric values properly from NumberInput
                  const principalStr = payment.principalPayment || '';
                  const principal = parseFloat(principalStr.replace(/[^\d.-]/g, '')) || 0;

                  // Convert percentage string to decimal for calculation
                  const rateStr = payment.interestRate || '';
                  const ratePercent = parseFloat(rateStr) || 0;
                  const rateDecimal = ratePercent / 100;

                  // Principal Interest: principal * (rate / 2) (180 days out of 360)
                  // Use Decimal for calculation to prevent floating-point errors
                  const principalInterest = new Decimal(principal)
                    .times(rateDecimal)
                    .dividedBy(2)
                    .toDecimalPlaces(2, Decimal.ROUND_HALF_UP)
                    .toNumber();

                  // Interest Payment: use calculated value if available, otherwise calculate simple interest
                  const calculatedInterest = calculatedInterestPayments.get(originalIndex);
                  const interestPayment =
                    calculatedInterest !== undefined
                      ? calculatedInterest
                      : new Decimal(principal)
                          .times(rateDecimal)
                          .toDecimalPlaces(2, Decimal.ROUND_HALF_UP)
                          .toNumber();

                  // Total Payment: principal + interest payment
                  const total = principal + interestPayment;

                  // Find fiscal year end date for this payment
                  const matchingDateRecord = availablePaymentDates.find((record) =>
                    record.paymentDates.includes(payment.paymentDate)
                  );
                  const fiscalYearEnd = matchingDateRecord?.fiscalYearEndDate;
                  const isLastPaymentInFiscalYear =
                    matchingDateRecord?.lastPaymentDateOfFiscalYear === payment.paymentDate;
                  const fiscalYearTotal =
                    isLastPaymentInFiscalYear && fiscalYearEnd
                      ? fiscalYearTotals.get(fiscalYearEnd)
                      : undefined;

                  return (
                    <Table.Row key={payment.paymentDate}>
                      <Table.Cell>
                        <Text fontSize="sm">{payment.paymentDate}</Text>
                      </Table.Cell>
                      <Table.Cell>
                        <PercentInput
                          ref={getInputRef(originalIndex, 1)}
                          value={payment.interestRate || ''}
                          onChange={(value) =>
                            handlePaymentChange(originalIndex, 'interestRate', value)
                          }
                          onBlur={() => {
                            handleInputBlur?.();
                            handleInterestRateBlur();
                          }}
                          onFocus={() => handleInputFocus?.(originalIndex, 1)}
                          onKeyDown={(e) => handleKeyDown(e, originalIndex, 1)}
                          size="sm"
                          isDisabled={isDisabled}
                          skipAutoSelect={isNavigating?.()}
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <CurrencyInput
                          ref={getInputRef(originalIndex, 2)}
                          value={payment.principalPayment}
                          onChange={(value) =>
                            handlePaymentChange(originalIndex, 'principalPayment', value)
                          }
                          onBlur={() => {
                            handleInputBlur?.();
                            handlePrincipalPaymentBlur(originalIndex);
                          }}
                          onFocus={() => handleInputFocus?.(originalIndex, 2)}
                          onKeyDown={(e) => handleKeyDown(e, originalIndex, 2)}
                          min={0}
                          size="sm"
                          isDisabled={isDisabled}
                          skipAutoSelect={isNavigating?.()}
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <Text fontSize="sm" color="gray.600">
                          {formatCurrency(principalInterest, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text fontSize="sm" color="gray.600">
                          {calculatedInterestPayments.has(originalIndex)
                            ? formatCurrency(interestPayment, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })
                            : formatCurrency(principal * rateDecimal, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text fontSize="sm" fontWeight="medium">
                          {formatCurrency(total, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </Text>
                      </Table.Cell>
                      {dateInputs && (
                        <Table.Cell>
                          <Text fontSize="sm" color="blue.600" fontWeight="medium">
                            {hasCalculatedReverseAmortization && fiscalYearTotal !== undefined
                              ? formatCurrency(fiscalYearTotal, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })
                              : formatCurrency(0, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                          </Text>
                        </Table.Cell>
                      )}
                    </Table.Row>
                  );
                })}

              {/* Footer Row with Running Totals */}
              {values.payments.length > 0 && (
                <Table.Row
                  fontWeight="bold"
                  bg="gray.50"
                  borderTop="2px solid"
                  borderTopColor="gray.200"
                >
                  <Table.Cell>
                    <Text fontSize="sm" fontWeight="bold">
                      TOTALS
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text fontSize="sm" color="gray.500">
                      —
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text fontSize="sm" fontWeight="bold">
                      {formatCurrency(runningTotals.totalPrincipal, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text fontSize="sm" color="gray.500">
                      —
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text fontSize="sm" fontWeight="bold">
                      {formatCurrency(runningTotals.totalInterest, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text fontSize="sm" fontWeight="bold" color="blue.600">
                      {formatCurrency(runningTotals.totalPayment, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Text>
                  </Table.Cell>
                  {dateInputs && (
                    <Table.Cell>
                      <Text fontSize="sm" color="gray.500">
                        —
                      </Text>
                    </Table.Cell>
                  )}
                </Table.Row>
              )}
            </Table.Body>
          </Table.Root>
        </Box>
      )}
    </Box>
  );
};
